import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isAfter, isBefore, parseISO } from 'date-fns';
import useIsOnline from '@lib/hooks/useIsOnline';
import usePrevious from '@lib/hooks/usePrevious';
import shouldFetchTechnicalPauses from '@lib/utils/shouldFetchTechnicalPauses';
import isAbortError from '@lib/utils/isAbortError';
import { captureException } from '@lib/utils/captureException';
import {
  onTechnicalPauseDetected,
  offTechnicalPauseDetected,
  TechnicalPauseErrorPayload,
} from '@lib/utils/technicalPauseErrorHandler';

export type TechnicalPause = {
  startAt: Date;
  endAt: Date;
  id: string;
};

const {
  VITE_MAINTENANCE_TRACKER_SECONDS: maintenanceTrackerSeconds,
  VITE_TECHNICAL_PAUSES_URL: technicalPausesUrl,
} = import.meta.env;

export default function useTechnicalPause() {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [upcomingTechnicalPause, setUpcomingTechnicalPause] =
    useState<TechnicalPause | null>(null);
  const [activeTechnicalPause, setActiveTechnicalPause] =
    useState<TechnicalPause | null>(null);

  const isOnline = useIsOnline();
  const { pathname, search } = useLocation();
  const prevPathname = usePrevious(pathname);
  const prevSearch = usePrevious(search);
  const isUrlChanged = prevSearch !== search || pathname !== prevPathname;
  const lastFetchTimeRef = useRef<Date | null>(null);
  const processRef = useRef<boolean>(false);

  const onProcessStart = useCallback(() => {
    processRef.current = true;
    lastFetchTimeRef.current = new Date();
    setLoading(true);
  }, []);

  const onProcessEnd = useCallback(() => {
    processRef.current = false;
    setLoading(false);
  }, []);

  useEffect(() => {
    const shouldFetch = shouldFetchTechnicalPauses({
      lastFetchTimeRef,
      maintenanceTrackerSeconds,
    });

    if (shouldFetch && !processRef.current) {
      onProcessStart();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      fetch(technicalPausesUrl, {
        cache: 'reload',
        signal: controller.signal,
      })
        .then((response) => response.json())
        .then((data) => {
          if (!data || !Array.isArray(data)) {
            throw new Error('Technical pauses fetch returned empty data');
          }
          const now = new Date();
          const parsedPauses = data.map((pause) => ({
            startAt: parseISO(pause.start_at),
            endAt: parseISO(pause.end_at),
            id: pause.id,
          }));

          const active = parsedPauses.find(
            (pause) =>
              isBefore(pause.startAt, now) && isAfter(pause.endAt, now),
          );
          const upcoming = parsedPauses
            .filter((pause) => isAfter(pause.startAt, now))
            .reduce(
              (earliest, pause) =>
                !earliest || isBefore(pause.startAt, earliest.startAt)
                  ? pause
                  : earliest,
              undefined as TechnicalPause | undefined,
            );
          setActiveTechnicalPause(active ?? null);
          setUpcomingTechnicalPause(upcoming ?? null);
          setFetched(true);
        })
        .catch((error) => {
          // Ignore abort errors from the 10s fetch timeout - not a real failure
          if (!isAbortError(error)) {
            captureException(error);
          }
          setActiveTechnicalPause(null);
          setUpcomingTechnicalPause(null);
          setFetched(true);
        })
        .finally(() => {
          clearTimeout(timeoutId);
          onProcessEnd();
        });
    }
  }, [isOnline, onProcessStart, isUrlChanged, onProcessEnd]);

  // Subscribe to Apollo error link detection for instant pause response
  useEffect(() => {
    const handlePauseDetected = (payload: TechnicalPauseErrorPayload) => {
      const endAt = parseISO(payload.endAt);
      if (Number.isNaN(endAt.getTime())) return;
      setActiveTechnicalPause((current) => {
        if (current) return current;
        return {
          startAt: new Date(),
          endAt,
          id: `graphql-detected-${Date.now()}`,
        };
      });
      setFetched(true);
    };

    onTechnicalPauseDetected(handlePauseDetected);
    return () => offTechnicalPauseDetected(handlePauseDetected);
  }, []);

  return { loading, fetched, upcomingTechnicalPause, activeTechnicalPause };
}
